import { supportedLanguages } from "@/i18n/languages";
import { PROFILE_URL } from "@/shared/environmentVariables";
import { ServerError } from "@/shared/errors";
import { FirebaseError } from "firebase/app";
import i18next, { i18n } from "i18next";
import { EMAIL_REGEX, FALLBACK_LANGUAGE_CODE } from "../shared/constants";
import { auth } from "./firebase";

export const isPasswordValid = (password: string): boolean => {
  return password.length >= 6;
};

export const isEmailValid = (email: string): boolean => EMAIL_REGEX.test(email);

export const isPhoneValid = (phone: string): boolean => {
  // general check that some phone is typed in
  return phone.length > 5;
};

export const changeLanguage = (i18n: i18n, languageCode: string) => {
  i18n.changeLanguage(languageCode);
};

export const getCachedLanguage = (i18n: i18n): string => {
  const cachedLanguage = supportedLanguages.find(
    ({ code }) => code === i18n.resolvedLanguage,
  );

  const fallbackLanguage = supportedLanguages.find(
    ({ code }) => code === FALLBACK_LANGUAGE_CODE,
  )!;

  return cachedLanguage?.code || fallbackLanguage.code;
};

export function connectToSuspense<T>(promise: Promise<T>) {
  let status = "pending";
  let response: T | Error;

  const suspender = promise.then(
    res => {
      status = "success";
      response = res;
    },
    err => {
      status = "error";
      response = err;
    },
  );

  const read = () => {
    switch (status) {
      case "pending":
        throw suspender;
      case "error":
        return response;
      default:
        return response;
    }
  };

  return { read };
}

export function appendPathSegments(
  inputUrl: URL,
  ...segments: ReadonlyArray<string | number>
): URL {
  const url = new URL(inputUrl.href);

  const cleaned = segments
    .filter(
      segment =>
        segment !== null && segment !== undefined && `${segment}` !== "",
    )
    .map(segment => String(segment).replace(/^\/+|\/+$/g, "")) // strip leading/trailing slashes
    .map(segment => encodeURIComponent(segment));

  const base = url.pathname.replace(/\/$/, ""); // drop trailing slash on base
  url.pathname = cleaned.length ? `${base}/${cleaned.join("/")}` : base;

  return url;
}

const createRedirectUrl = (invitationToken?: string | null): string => {
  const currentUrl = new URL(window.location.href);
  const urlToken = currentUrl.searchParams.get("invitationToken");
  const tokenToUse = invitationToken || urlToken;

  const refEmailSearchParam = currentUrl.searchParams.get("refEmail");
  const refEmailSessionStorage = sessionStorage.getItem("refEmail");
  const refEmail = refEmailSessionStorage || refEmailSearchParam;

  // Формируем URL для перенаправления
  let redirectUrl = new URL(PROFILE_URL);

  if (tokenToUse) {
    redirectUrl.searchParams.set("invitationToken", tokenToUse);
  }

  if (refEmail) {
    redirectUrl.searchParams.set("refEmail", refEmail);
    redirectUrl = appendPathSegments(redirectUrl, "create-company");
  }

  return redirectUrl.href;
};

export const onLoginSuccess = (invitationToken?: string | null) => {
  const redirectUrl = createRedirectUrl(invitationToken);

  window.location.href = redirectUrl;
  auth.signOut();
};

export const translateError = (error: unknown): string => {
  let translatedError = i18next.t("errors.unknownError");
  if (error instanceof FirebaseError) {
    translatedError = i18next.t(`errors.${error.code}`);
  }
  if (error instanceof ServerError) {
    translatedError = error.message;
  }

  return translatedError;
};

/**
 * Check if user is from Russia using IP geolocation
 * @returns Promise<boolean> - true if user is from Russia, false otherwise
 */
export const isUserFromRussia = async (): Promise<boolean> => {
  try {
    const response = await fetch("https://ipinfo.io/json");
    if (response.ok) {
      const data = await response.json();
      return data.country === "RU";
    }

    return false;
  } catch (error) {
    console.error("Error detecting user location:", error);
    // In case of error, assume user is not from Russia to avoid blocking
    return false;
  }
};
